import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SelectedPanelEnum } from '@app/core';
import { productNameConfig } from '@app/my-zone/config/product-name.config';
import { InsurancesQueryParamsInterface } from '@app/my-zone/interfaces/insurances-query-params.interface';
import {
    DateService,
    DateStruct,
    ErrorInterface,
    InsurancePolicyActionService,
    NewModal as ModalService,
    PolicyProductIdEnum,
    PurchasedProductInterface,
} from 'outshared-lib';
import { Subscription } from 'rxjs';
import { EndDateIndicationEnum } from '../insurance-end-product-form/end-date-indication.enum';
import { InsuranceEndProductSuccessModal } from '../insurance-end-product-success-modal';
import { EndProductReasonEnum } from './end-product-reason.enum';
import {
    WucBalloonModule,
    WucButtonModule,
    WucCardModule,
    WucFormFieldLabelModule,
    WucFormFieldModule,
    WucIconUiModule,
    WucInputCheckboxModule,
    WucInputDatePickerModule,
    WucInputRadioModule,
    WucListModule
} from '@inshared/website-ui-components';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';

@Component({
    standalone: true,
    selector: 'ins-insurance-end-product-vehicle-form',
    templateUrl: 'insurance-end-product-vehicle-form.component.html',
    imports: [
        NgForOf,
        NgIf,
        NgSwitch,
        NgSwitchCase,
        ReactiveFormsModule,
        WucButtonModule,
        WucCardModule,
        WucFormFieldLabelModule,
        WucFormFieldModule,
        WucIconUiModule,
        WucInputCheckboxModule,
        WucInputDatePickerModule,
        WucInputRadioModule,
        WucListModule,
        WucBalloonModule,
    ],
})
export class InsuranceEndProductVehicleFormComponent implements OnDestroy, OnInit {
    @Input() purchasedProduct!: PurchasedProductInterface;

    public form: UntypedFormGroup;
    public isDisabled: boolean;
    public policyProductEnum: typeof PolicyProductIdEnum;
    public productName: string;
    public endDateIndicationEnum: typeof EndDateIndicationEnum;
    public endProductReasonEnum: typeof EndProductReasonEnum;
    public errors: ErrorInterface[];
    public minimumDate: DateStruct;

    private subscriptions = new Subscription();

    constructor(
        private dateService: DateService,
        private insurancePolicyActionService: InsurancePolicyActionService,
        private router: Router,
        private modalService: ModalService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        this.isDisabled = false;
        this.policyProductEnum = PolicyProductIdEnum;
        this.endDateIndicationEnum = EndDateIndicationEnum;
        this.endProductReasonEnum = EndProductReasonEnum;
        this.errors = [];
    }

    public ngOnInit(): void {
        this.createForm();
        this.watchEndDateControl();
        this.watchEndDateIndicationControl();
        this.setMinimumDate();
        this.setProductName();
    }

    public get reason(): AbstractControl {
        return this.form.get('reason');
    }

    public get endDate(): AbstractControl {
        return this.form.get('endDate');
    }

    public get endDateIndication(): AbstractControl {
        return this.form.get('endDateIndication');
    }

    public get suspendIndication(): FormControl {
        return this.form.get('suspendIndication') as FormControl;
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        const endDate =
            this.endDateIndication.value === EndDateIndicationEnum.Immediately
                ? this.dateService.add(1, 'day')
                : this.dateService.add(this.endDate.value, 1, 'day');

        this.insurancePolicyActionService
            .endProduct$({
                endDate,
                productId: this.purchasedProduct.productId,
                productSequenceNumber: this.purchasedProduct.productSequenceNumber,
            })
            .subscribe({
                next: () => this.onEndProductSuccess(endDate),
                error: (errors) => this.onEndProductError(errors),
            });

        this.isDisabled = true;
        this.errors = [];
    }

    private onEndProductSuccess(endDate: string): void {
        this.isDisabled = false;
        this.errors = [];
        this.closePanel();
        this.openInsuranceEndProductModal(endDate);
        this.changeDetectorRef.detectChanges();
    }

    private onEndProductError(errors: ErrorInterface[]): void {
        this.errors = errors;
        this.isDisabled = false;
        this.changeDetectorRef.detectChanges();
    }

    private closePanel(): void {
        const queryParams: InsurancesQueryParamsInterface = {
            panel: SelectedPanelEnum.Close,
            productId: this.purchasedProduct.productId,
            productSequenceNumber: this.purchasedProduct.productSequenceNumber,
            scroll: true,
        };

        this.router.navigate([], {
            queryParams,
            queryParamsHandling: 'merge',
        });
    }

    private openInsuranceEndProductModal(endDate: string): void {
        // wait for scroll before opening modal
        setTimeout(() => {
            const insuranceEndProductModalRef = this.modalService.open(InsuranceEndProductSuccessModal);
            insuranceEndProductModalRef.componentInstance.purchasedProduct = this.purchasedProduct;
            insuranceEndProductModalRef.componentInstance.endDate = endDate;
        }, 1000);
    }

    private setProductName(): void {
        this.productName = productNameConfig[this.purchasedProduct?.productId] ?? '';
    }

    private createForm(): void {
        this.form = new UntypedFormGroup({
            reason: new FormControl(''),
            endDate: new FormControl('', [Validators.required]),
            endDateIndication: new FormControl('', [Validators.required]),
            suspendIndication: new FormControl(false),
        });
    }

    private watchEndDateIndicationControl(): void {
        const endDateIndicationChanged = this.endDateIndication.valueChanges.subscribe((value) =>
            this.endDate.setValue(value === EndDateIndicationEnum.Immediately ? this.getDateTomorrow() : null)
        );

        this.subscriptions.add(endDateIndicationChanged);
    }

    private getDateTomorrow(): string {
        return this.dateService.add(1, 'day');
    }

    private watchEndDateControl(): void {
        const endDateValueChanges = this.endDate.valueChanges.subscribe(() => (this.isDisabled = false));

        this.subscriptions.add(endDateValueChanges);
    }

    private setMinimumDate(): void {
        this.minimumDate = this.dateService.toNgbDateStruct(this.dateService.add(1, 'day'));
    }
}
